import type { TableProps } from 'antd'
import type { InputMaybe, Paginate } from '~/gql/generated/graphql'
import type { Pagination } from '~/types'

type TableQueryData = { table?: { nodes: unknown[] } | null }
type TableQueryResult = { data?: TableQueryData }

type QueryNodes<TResult extends TableQueryResult> = NonNullable<
  NonNullable<TResult['data']>['table']
>['nodes']

export type TableData<TNode> = {
  table: {
    nodes: TNode[]
    pagination: Pagination & { showSizeChanger: boolean }
  }
}

/**
 * Wraps a generated list query hook to return its result in the shape the
 * Table component expects. Options and result types are read off the hook
 * that's passed in, so callers keep their own query's types.
 *
 * We're skipping the count on these queries for performance, so there is no
 * total to build a pager from — merge the nodes with a static pagination
 * object instead. Nodes stand in as an empty list until the first result
 * lands.
 */
export function useTableQuery<TOptions, TResult extends TableQueryResult>(
  useQuery: (options: TOptions) => TResult,
  options: TOptions & { variables: { paginate?: InputMaybe<Paginate> } },
): { data: TableData<QueryNodes<TResult>[number]> } & Omit<TResult, 'data'> {
  const { data, ...rest } = useQuery(options)

  const pageSize = options.variables.paginate?.per_page ?? 20
  const current = options.variables.paginate?.page ?? 1

  return {
    data: {
      table: {
        nodes: (data?.table?.nodes ?? []) as QueryNodes<TResult>,
        pagination: {
          pageSize,
          current,
          // Ha! tricking you, there might be more, but click to find out
          total: pageSize * current + 1,
          // Default on if total > 50 but we can't know that on page 1
          showSizeChanger: true,
        } satisfies TableProps<unknown>['pagination'],
      },
    },
    ...rest,
  }
}
